let accessToken: string | null = null;

// Cross-tab communication: BroadcastChannel with storage-event fallback
const authChannel: BroadcastChannel | null = (typeof window !== 'undefined' && (window as any).BroadcastChannel)
  ? new BroadcastChannel('auth')
  : null;

function broadcastAuthEvent(type: 'login' | 'logout') {
  try { authChannel?.postMessage({ type, ts: Date.now() }); } catch {}
  try {
    // storage event fallback for browsers without BroadcastChannel
    localStorage.setItem('auth-event', JSON.stringify({ type, ts: Date.now() }));
    // remove quickly so it behaves like a pulse
    setTimeout(() => { try { localStorage.removeItem('auth-event'); } catch {} }, 0);
  } catch {}
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function clearAuth() {
  accessToken = null;
  // We no longer store access tokens in localStorage, but keep cleanup to be safe
  try { localStorage.removeItem('token'); } catch {}
  try { localStorage.removeItem('orgId'); } catch {}
  try { localStorage.removeItem('orgName'); } catch {}
}

export function notifyLoginAcrossTabs() {
  broadcastAuthEvent('login');
}

export function notifyLogoutAcrossTabs() {
  broadcastAuthEvent('logout');
}

// Listen for cross-tab logout/login and react locally
if (typeof window !== 'undefined') {
  authChannel?.addEventListener('message', (evt: MessageEvent) => {
    const { type } = (evt && evt.data) || {};
    if (type === 'logout') {
      clearAuth();
    } else if (type === 'login') {
      // On login from another tab, proactively try to obtain an access token via refresh
      // so this tab becomes immediately usable without reload
      refreshAccessToken().then((t) => { if (t) setAccessToken(t); }).catch(() => {});
    }
  });
  window.addEventListener('storage', (e) => {
    if (e.key === 'auth-event' && e.newValue) {
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed?.type === 'logout') { clearAuth(); }
        if (parsed?.type === 'login') { refreshAccessToken().then((t) => { if (t) setAccessToken(t); }).catch(() => {}); }
      } catch {}
    }
  });
}

// Lock to prevent concurrent refresh calls
let refreshPromise: Promise<string | null> | null = null;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN_MS = 1000; // Don't refresh more than once per second

export async function refreshAccessToken(apiUrl = import.meta.env.VITE_API_BASE): Promise<string | null> {
  // If there's already a refresh in progress, return that promise
  if (refreshPromise) {
    return refreshPromise;
  }

  // If we just refreshed recently, return the current token (if any)
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_COOLDOWN_MS && accessToken) {
    return accessToken;
  }

  // Create a new refresh promise
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${apiUrl}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        // If we get a 429, wait a bit before allowing another refresh
        if (res.status === 429) {
          lastRefreshTime = now + 5000; // Wait 5 seconds after rate limit
        }
        return null;
      }
      const body = await res.json();
      if (body?.accessToken) {
        setAccessToken(body.accessToken);
        lastRefreshTime = now;
        return body.accessToken;
      }
    } catch (e) { 
      // ignore 
    } finally {
      // Clear the promise after a short delay to allow retries if needed
      setTimeout(() => {
        refreshPromise = null;
      }, REFRESH_COOLDOWN_MS);
    }
    return null;
  })();

  return refreshPromise;
}

// Optional: schedule a background refresh slightly before access token expiry to reduce 401s.
// We do not parse JWT without verification; use a safe best-effort decode.
let refreshTimer: number | null = null;
export function scheduleAccessTokenRefresh(token: string) {
  try {
    const payloadBase64 = token.split('.')[1];
    const payload = JSON.parse(atob(payloadBase64));
    const expSec = Number(payload?.exp);
    if (!expSec) return;
    const expMs = expSec * 1000;
    const now = Date.now();
    const leadMs = 60 * 1000; // refresh 60s before expiry
    const delay = Math.max(0, expMs - now - leadMs);
    if (refreshTimer) {
      clearTimeout(refreshTimer as any);
      refreshTimer = null;
    }
    refreshTimer = (setTimeout(async () => {
      const newToken = await refreshAccessToken();
      if (newToken) scheduleAccessTokenRefresh(newToken);
    }, delay) as unknown) as number;
  } catch {}
}

export async function logout(apiUrl = import.meta.env.VITE_API_BASE): Promise<void> {
  try { await fetch(`${apiUrl}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
  clearAuth();
  notifyLogoutAcrossTabs();
}

/**
 * A wrapper around fetch that automatically handles:
 * 1. Injecting the access token from memory
 * 2. Refreshing the token if it's missing or expired (401)
 * 3. Retrying the request once after refresh
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  let token = getAccessToken();
  
  // If no token in memory, try a refresh first
  if (!token) {
    token = await refreshAccessToken();
  }

  const makeRequest = (t: string | null) => {
    const headers = new Headers(options.headers || {});
    if (t) {
      headers.set('Authorization', `Bearer ${t}`);
    }
    // Ensure credentials include cookies for the refresh token
    return fetch(url, { ...options, headers, credentials: 'include' });
  };

  let res = await makeRequest(token);

  // If we get a 401, the token in memory might have expired
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry once with the new token
      res = await makeRequest(newToken);
    }
  }

  return res;
}

